import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import * as M from './multiSelectStyles';

export interface MultiSelectOption {
  value: string | number;
  label: string;
}

interface MultiSelectProps {
  id?: string;
  options: MultiSelectOption[];
  selected: Array<string | number>;
  onChange: (selected: Array<string | number>) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  options,
  selected,
  onChange,
  placeholder = 'Selecione',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string | number) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectedOptions = options.filter((o) => selected.includes(o.value));

  return (
    <M.Container ref={containerRef} id={id}>
      <M.SelectInput
        onClick={() => !disabled && setIsOpen((v) => !v)}
        disabled={disabled}
      >
        <M.ChipsContainer>
          {selectedOptions.length === 0 ? (
            <M.Placeholder>{placeholder}</M.Placeholder>
          ) : (
            selectedOptions.map((option) => (
              <M.Chip key={option.value}>
                {option.label}
                <M.ChipRemove
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOption(option.value);
                  }}
                  aria-label={`Remover ${option.label}`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </M.ChipRemove>
              </M.Chip>
            ))
          )}
        </M.ChipsContainer>
        <M.IconsContainer>
          {selectedOptions.length > 0 && (
            <M.ClearButton
              onClick={clearAll}
              aria-label="Limpar seleção"
              type="button"
            >
              <FontAwesomeIcon icon={faTimes} />
            </M.ClearButton>
          )}
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </M.IconsContainer>
      </M.SelectInput>

      {isOpen && (
        <M.OptionsContainer role="listbox">
          {options.length > 0 && (
            <M.OptionsHeader>
              <M.HeaderInfo>
                {selected.length} de {options.length} selecionado
                {selected.length === 1 ? '' : 's'}
              </M.HeaderInfo>
              <M.HeaderAction
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (selected.length === options.length) {
                    onChange([]);
                  } else {
                    onChange(options.map((o) => o.value));
                  }
                }}
              >
                {selected.length === options.length
                  ? 'Limpar tudo'
                  : 'Selecionar todos'}
              </M.HeaderAction>
            </M.OptionsHeader>
          )}
          {options.length === 0 ? (
            <M.NoOptions>Nenhuma opção</M.NoOptions>
          ) : (
            options.map((option) => {
              const isChecked = selected.includes(option.value);
              return (
                <M.Option
                  key={option.value}
                  isSelected={isChecked}
                  onClick={() => toggleOption(option.value)}
                  role="option"
                  aria-selected={isChecked}
                >
                  <M.Checkbox checked={isChecked} readOnly />
                  <M.OptionLabel>{option.label}</M.OptionLabel>
                </M.Option>
              );
            })
          )}
        </M.OptionsContainer>
      )}
    </M.Container>
  );
};

export default MultiSelect;
